import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs } from "../../../../ui/components/Tabs";
import { GlsSection } from "../../../gls";
import { CustomersMastersTab } from "./CustomersMastersTab";
import { VendorsMastersTab } from "./VendorsMastersTab";
import { ComplianceMastersTab } from "./ComplianceMastersTab";

type MastersSubTab = "gl" | "customers" | "vendors" | "compliance";

export const MastersSection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<MastersSubTab>("gl");

  useEffect(() => {
    const stateTab = (location.state as any)?.mastersTab as
      | MastersSubTab
      | undefined;
    const qs = new URLSearchParams(location.search);
    const queryTab = (qs.get("tab") as MastersSubTab | null) ?? null;
    const nextTab = queryTab ?? stateTab ?? null;

    if (nextTab && ["gl", "customers", "vendors", "compliance"].includes(nextTab)) {
      setActiveTab(nextTab);
      // Clear navigation state so it doesn't re-apply on back/forward.
      if (stateTab) {
        navigate(".", { replace: true, state: {} });
      }
    }
  }, [location.search, location.state, navigate]);

  const subtitle = useMemo(() => {
    switch (activeTab) {
      case "gl":
        return "Manage chart-of-account mappings";
      case "customers":
        return "Browse customers synced in your workspace";
      case "vendors":
        return "Browse vendors synced in your workspace";
      case "compliance":
        return "Manage LUT & export compliance";
      default:
        return "Manage master data";
    }
  }, [activeTab]);

  const handleTabClick = (tab: MastersSubTab) => {
    setActiveTab(tab);
    const qs = new URLSearchParams(location.search);
    if (tab === "gl") {
      qs.delete("tab");
    } else {
      qs.set("tab", tab);
    }
    const query = qs.toString();
    navigate(
      {
        pathname: location.pathname,
        search: query ? `?${query}` : "",
      },
      { replace: true },
    );
  };

  return (
    <div className="flex w-full flex-col items-start">
      <div className="flex w-full flex-col items-start gap-6 border-b border-solid border-neutral-border bg-default-background p-8">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Masters
          </span>
          <span className="text-body font-body text-subtext-color">
            {subtitle}
          </span>
        </div>

        <Tabs>
          <Tabs.Item
            active={activeTab === "gl"}
            onClick={() => handleTabClick("gl")}
          >
            GL
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "customers"}
            onClick={() => handleTabClick("customers")}
          >
            Customers
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "vendors"}
            onClick={() => handleTabClick("vendors")}
          >
            Vendors
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "compliance"}
            onClick={() => handleTabClick("compliance")}
          >
            Compliance
          </Tabs.Item>
        </Tabs>
      </div>

      <div className="w-full bg-default-background">
        {activeTab === "gl" ? (
          <GlsSection />
        ) : (
          <div className="w-full p-8">
            {activeTab === "customers" ? (
              <CustomersMastersTab />
            ) : activeTab === "vendors" ? (
              <VendorsMastersTab />
            ) : (
              <ComplianceMastersTab />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
